import React, {useContext, useState, useEffect, useRef} from "react";
import DynamicContent from "../../nav/DynamicContent";
import axios from "axios";
import {Form, Select} from "antd";
import Button from "@common/Button";
import {BrandContext} from "@context/BrandContext";
import TableAntD from "@common/TableAntD";
import TrailOverviewStage from "./TrailOverviewStage";
import TrailAssignmentProgressGraph from "./TrailAssignmentProgressGraph";
import TableFilterControls from "@common/TableFilterControls";
import TableFilterControlsSection from "@common/TableFilterControlsSection";
import TableFilterControlsForm from "@common/TableFilterControlsForm";

const TrailAssignmentProgress = props => {
    const {trailAssignmentId, onExit} = props;
    const brandConfig = useContext(BrandContext);
    const [loading, setLoading] = useState(true);
    const [trailAssignment, setTrailAssignment] = useState(null);
    const [trailConcepts, setTrailConcepts] = useState(null);
    const [highlightUserId, setHighlightUserId] = useState(null);
    const [highlightConceptId, setHighlightConceptId] = useState(null);
    const [resultsTable, setResultsTable] = useState({});
    const [stepResult, setStepResult] = useState(null);
    const pageRef = useRef();

    useEffect(() => {
        if (trailAssignmentId == null) return;

        axios.get(`/web/ui/trail/progress/${trailAssignmentId}`)
            .then((response) => {
                setTrailAssignment(response.data.trailAssignment);
            });
    }, []);
    useEffect(() => {
        if (trailAssignment == null) return;

        loadTrailData(trailAssignment.pathwayId);
    }, [trailAssignment]);
    useEffect(() => {
        if (trailAssignment == null) return;

        loadTrailAssignmentResults();
    }, [trailAssignment, highlightUserId, highlightConceptId]);

    const loadTrailData = (trailId) => {
        setLoading(true);
        axios.get("/trail/loadTrailData.action", {params: {pathwayId: trailId}})
            .then((response) => {
                if (response.data.success) {
                    window.pathway = response.data.core;
                    window.pathsObj = response.data.paths;
                    window.resourcesData = response.data.resources;
                    window.pathwayBuilder.loadTracker(pageRef.current);
                    setTrailConcepts(response.data.pathwayConcepts);
                } else {
                    console.error('Error loading trail data', trailId, response.data);
                }
            })
            .catch(error => {
                console.error('Error loading trail data', trailId, error);
            })
            .finally(() => {
                setLoading(false);
            })
    }
    const loadTrailAssignmentResults = () => {
        const params = {
            pathwayAssignmentId: trailAssignmentId,
            ...(highlightUserId && { userId: highlightUserId }),
            // ...(trailAssignment?.concept?.conceptId && { conceptId: trailAssignment?.concept?.conceptId })
            ...(highlightConceptId && { conceptId: highlightConceptId })
        }
        axios.get("/trail/trailResults.action", {params: params})
            .then((response) => {
                window.pathwayBuilder.updateResults(response.data.pathwayResults, highlightUserId);

                const results = trailAssignment.students.reduce((acc, student) => {
                    const userResults = response.data.pathwayResults.userResults.filter(
                        result => result.userId === student.userId
                    );
                    if (userResults.length > 0) acc[student.userId] = userResults;
                    return acc;
                }, {});

                let newResultsTable = {
                    columns: [],
                    data: []
                };
                if (highlightUserId == null) {
                    newResultsTable.columns = [
                        {title: "Students (* task was skipped)", key: "user", dataIndex: "user"},
                        {title: "Date", key: "date", dataIndex: "date"},
                        {title: "Results", key: "results", dataIndex: "results"},
                    ];
                    trailAssignment.students.map(student => {
                        if (results[student.userId]) {
                            const editDates = results[student.userId]
                                .map(result => result.formattedDate)
                                .sort((a, b) => b - a)[0];
                            newResultsTable.data.push({
                                key: student.userId,
                                user: <a href="#" role="button" onClick={e => {
                                    e.preventDefault();
                                    setHighlightUserId(student.userId)
                                }}>{student.fullName}</a>,
                                date: editDates ?? "N/A",
                                results: <TrailAssignmentProgressGraph
                                    results={results[student.userId].filter(result => !result.endStep)}
                                    onClick={(userResult) => {
                                        loadStepResults(userResult, student.userId);
                                    }}
                                />
                            });
                        }
                    });
                } else {
                    newResultsTable.columns = [
                        {title: "Step", key: "step", dataIndex: "step"},
                        {title: "Date", key: "date", dataIndex: "date"},
                        {title: "Section", key: "section", dataIndex: "section"},
                        {title: "Results", key: "results", dataIndex: "results"},
                    ];
                    results[highlightUserId]?.filter(result => !result.endStep).map(userResult => {
                        let stepJSX = <span>{userResult.pathwayActivityName}</span>;
                        let resultsJSX = <></>;
                        const handleLoadStepResults = (e, userResult) => {
                            e.preventDefault();
                            loadStepResults(userResult);
                        }
                        if (userResult.stepActivities && userResult.stepActivities.length > 0) {
                            // $dateTd.text(result.formattedDate);
                            // $conceptTd.html(result.stepContentName);

                            stepJSX = (
                                <ul className="ch-list">
                                    {userResult.stepActivities.map((stepActivity, i) => (
                                        <li key={i}>{stepActivity.stepActivityName}</li>
                                    ))}
                                </ul>
                            );
                            resultsJSX = (
                                <ul className="ch-list">
                                    {userResult.stepActivities.map((stepActivity, i) => {
                                        let resultLabel = "N/A";
                                        if (stepActivity.markState === "SELF_MARKED") {
                                            if (stepActivity.answeredPercent) {
                                                resultLabel = `${stepActivity.answeredPercent}% complete`;
                                            } else {
                                                resultLabel = "0% complete";
                                            }
                                        } else if(stepActivity.markState === "AUTO_MARKED" || stepActivity.markState === "MARKED") {
                                            resultLabel = `${stepActivity.totalMark}/${stepActivity.maxMark} (${stepActivity.score}%)`;
                                        } else if(userResult.skipStep) {
                                            resultLabel = "Skipped";
                                        }
                                        return (
                                            <li key={i}>
                                                <a href="#" role="button" onClick={e => handleLoadStepResults(e, userResult)}>{resultLabel}</a>
                                            </li>
                                        );
                                    })}
                                </ul>
                            );
                        } else if(userResult.resourceType === "SURVEY") {
                            resultsJSX = (
                                <div>
                                    <div>
                                        <a href="#" role="button" onClick={e => handleLoadStepResults(e, userResult)}>{userResult.noContent ? "N/A" : "Completed"}</a>
                                    </div>
                                    {userResult.mark != null && (
                                        <div style={{whiteSpace: "nowrap"}}>
                                            {Array.from({length: userResult.maxMark}).map((_, i) => (
                                                <div key={i} className={`mark-given disabled mark-small ${(userResult.mark === (i+1) ? "mark-selected"+i : "")}`}>&nbsp;</div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            );
                        } else {
                            if (!userResult.skipStep) {
                                let resultLabel;
                                if (userResult.noContent) {
                                    resultLabel = "N/A";
                                } else if (userResult.score == null) {
                                    resultLabel = "Completed";
                                } else {
                                    resultLabel = `${userResult.mark}/${userResult.maxMark} (${userResult.score}%)`;
                                }
                                resultsJSX = (
                                    <a href="#" role="button" onClick={e => handleLoadStepResults(e, userResult)}>
                                        {resultLabel}
                                    </a>
                                )
                            } else {
                                resultsJSX = <span>Skipped</span>;
                            }
                        }

                        newResultsTable.data.push({
                            key: userResult.stepUserId,
                            step: stepJSX,
                            date: userResult.formattedDate,
                            section: (userResult.resourceType !== "SURVEY" ? userResult.stepContentName : ""),
                            results: resultsJSX
                        });
                    });
                }
                setResultsTable(newResultsTable);
            });
    }
    const loadStepResults = (userResult, userId) => {
        const params = {
            userId: userId ? userId : highlightUserId,
            stepId: userResult.stepId,
            pathwayActivityId: userResult.pathwayActivityId,
            pathwayAssignmentId: trailAssignmentId,
            orderByLevelThenSeq: true,
            ...(userResult.conceptId && {conceptId: userResult.conceptId})
        }
        if (userId) setHighlightUserId(userId);
        axios.get("/trail/getStudentStepResults.action", {params: params})
            .then((response) => {
                if (response.data.success || response.data.userSkipped) {
                    setStepResult(
                        <>
                            <div className="margin-b--l">
                                <div>{response.data.studentInfo.fullName}</div>
                                <div><b>Results:</b> {response.data.stepUser.stepName} {response.data.stepUser.noConcept ? '' : '- ' + response.data.stepUser.stepContentName}</div>
                                {response.data.stepUser.skipReason === "USER" && (
                                    <div className="errorMessage">{response.data.stepUser.repeatCount} from {response.data.stepUser.repeatTotal} {response.data.stepUser.stepName} attempted before being skipped.</div>
                                )}
                            </div>
                            <DynamicContent
                                url={response.data.tab.resultUrl}
                                params={{
                                    ...params,
                                    pathwayUserId: response.data.pathwayUserId,
                                    conceptId: response.data.stepUser.conceptId,
                                    ...(response.data.quizId && !response.data.userSkipped && {
                                        quizId: response.data.quizId,
                                        quizResultId: response.data.quizResultId,
                                    })
                                }}
                                callback={() => {
                                    const resultLoadFunction = window[response.data.tab.resultLoadFunction];
                                    if (resultLoadFunction && typeof resultLoadFunction === "function") resultLoadFunction(params);
                                }}
                            />
                        </>
                    )
                }
            });
    }

    globalReactFunctions.loadTrailStepResults = loadStepResults;

    const exitBuilder = () => {
        if (onExit && typeof onExit === "function") onExit();
    }

    if (trailAssignmentId == null || trailAssignment == null) return <></>

    return (
        <div ref={pageRef} className="padding-y--l">
            <div className="pull-right">
                <Button onClick={() => {
                    if (stepResult) {
                        setStepResult(null);
                    } else {
                        exitBuilder();
                    }
                }}>Back</Button>
            </div>
            <div className={stepResult ? "ch-hideVis" : ""}>
                <div>
                    <h2 className="margin-b--m clearfix">{trailAssignment.pathwayAssignmentName}</h2>
                    <TableFilterControlsForm>
                        <TableFilterControlsSection showDivider={false}>
                            <Form.Item
                                label="Results by"
                            >
                                <Select
                                    style={{minWidth: "140px"}}
                                    value={highlightUserId ?? ""}
                                    onChange={(userId) => setHighlightUserId(userId || null)}
                                >
                                    <Select.Option value="">All</Select.Option>
                                    {trailAssignment.students.map(student => (
                                        <Select.Option key={student.userId} value={student.userId}>
                                            {student.fullName}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                            {(trailAssignment.topic || trailAssignment?.concepts.length > 1) && (
                                <Form.Item
                                    label={brandConfig.conceptName}
                                >
                                    <Select
                                        style={{minWidth: "140px"}}
                                        value={highlightConceptId ?? ""}
                                        onChange={(conceptId) => setHighlightConceptId(conceptId || null)}
                                    >
                                        <Select.Option value="">All</Select.Option>
                                        {trailAssignment.concepts.map(concept => (
                                            <Select.Option key={concept.conceptId} value={concept.conceptId}>
                                                {concept.conceptName}
                                            </Select.Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            )}
                        </TableFilterControlsSection>
                    </TableFilterControlsForm>
                    <div className="margin-b--l">
                        <h3>Overview</h3>
                        {trailAssignment?.pathwayType === "TASK" && (
                            <div>
                                <div>This task contains content from:</div>
                                {trailConcepts && trailConcepts.length > 0 && (
                                    <ul>
                                        {trailConcepts?.map(concept => (
                                            <li key={concept.conceptId}>
                                                {concept.topicName} &#8658; {concept.conceptName}({brandConfig.conceptName.toLowerCase()})
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        )}
                    </div>
                </div>
                <TrailOverviewStage
                    trailDetails={{
                        pathwayAssignmentId: trailAssignmentId,
                        pathwayType: trailAssignment.pathwayType,
                        conceptId: trailAssignment?.concept?.conceptId ?? null,
                    }}
                    progressMode={true}
                />
                <div>
                    <h3 className="margin-t--l margin-b--m">Results</h3>
                    <TableAntD
                        columnData={resultsTable?.columns}
                        dataSource={resultsTable?.data}
                    />
                </div>
            </div>
            {stepResult && (
                <div>{stepResult}</div>
            )}
        </div>
    )
}

export default TrailAssignmentProgress;